<script>
	import { getFunctions, httpsCallable } from "firebase/functions";
	import { capitalizeEachWord } from "cando-util";

	import { getPdfTags } from "./getPdfTags.svelte";
	import { updatePdfUrl } from "./updatePdfUrl";
	import { store } from "$lib/utilities/store";

	import SvelteFa from "$lib/components/SvelteFa.svelte";
	import {
		faCircleCheck,
		faTrashCan,
	} from "@fortawesome/free-solid-svg-icons";

	import Select from "$lib/components/Select.svelte";
	import Loading from "$lib/components/Loading.svelte";
	import DocumentPreview from "./DocumentPreview.svelte";
	import TemplateFields from "./TemplateFields.svelte";

	export let value = {};
	export let question;
	export let handleChange = () => {};
	export let readonly = false;
	export let label;
	export let hideIframe = false;

	let newTemplate = "";
	const deleteConfirm = {};

	$: allFiles = [
		...new Set([
			...Object.entries($store?.templates?.docs || {}).map(
				([k, v]) => v.name,
			),
		]),
	].sort();
	$: options = [
		"",
		...(allFiles || []).filter(
			(template) => value?.[`${template}.pdf`] === undefined,
		),
	].sort();

	let loading = false;
	const handleTemplateSelect = async (e) => {
		if (!newTemplate) return;
		loading = true;
		const templateId = Object.entries($store?.templates?.docs || {}).filter(
			([k, v]) => v.name === newTemplate,
		)[0][0];
		const functions = getFunctions();
		const callable = httpsCallable(functions, "getTemplate");
		const response = await callable({ templateId });

		console.log({ newTemplate, templateId, response });

		const key = `${newTemplate}.pdf`;

		const file = {
			name: `${newTemplate}.pdf`,
			...response.data,
			templateUrl: response.data.dataUrl,
		};

		const {
			tags,
			fieldLocations,
			leftMargin,
			signatureTags,
			signatureLocations,
			signatureDateLocations,
		} = await getPdfTags(file);

		console.log(`${tags.split("-|-").length} tags found and loaded`);

		file.tags = tags;
		file.fieldLocations = fieldLocations;
		file.leftMargin = leftMargin;
		file.signatureTags = signatureTags;
		file.signatureLocations = signatureLocations;
		file.signatureDateLocations = signatureDateLocations;
		file.index = Object.keys(value || {}).filter(
			(v) => value[v].type !== undefined,
		).length;

		value = {
			...value,
			[key]: file,
		};

		await updateValues();

		newTemplate = "";

		handleChange(question);

		loading = false;
	};

	$: previewFiles = Object.keys(value || {})
		.filter((v) => value[v].type !== undefined)
		.map((v) => value[v])
		.sort((a, b) => (a.index < b.index ? -1 : 1));

	const deleteDocumentFunction = async (e) => {
		const fileName = e.target.closest("button").getAttribute("key");
		if (!deleteConfirm[fileName]) {
			deleteConfirm[fileName] = true;
			setTimeout(() => {
				deleteConfirm[fileName] = false;
			}, 5000);
			return;
		}

		const key = `${fileName}`;
		delete value[key];

		await updateValues();

		handleChange(question);

		deleteConfirm[fileName] = false;
	};

	const updateValues = async () => {
		// let's redo the indexes;
		const files = Object.keys(value)
			.filter((key) => value[key].type !== undefined)
			.map((fileKey) => value[fileKey])
			.sort((a, b) => (a.index < b.index ? -1 : 1));
		const documentTagsAry = [];
		const documentSignatureTags = [];
		let i = 0;
		for (const file of files) {
			file.index = i;
			i++;
			const tags = file.tags.split("-|-");
			const arys = [
				{ source: tags, target: documentTagsAry },
				{ source: file.signatureTags, target: documentSignatureTags },
			];
			for (const ary of arys) {
				for (const tag of ary.source) {
					if (!ary.target.includes(tag)) ary.target.push(tag);
				}
			}

			// this is gonna hold when a specific file was viewed and by which party in the /sign/ route
			if (file.viewed === undefined) {
				file.viewed = {};
			}

			const { hash, dataUrl } = await updatePdfUrl(
				file,
				value.documentValues,
			);
			file.dataUrl = dataUrl;
			file.hash = hash;
		}

		value.documentTags = documentTagsAry.join("-|-");
		value.documentSignatureTags = [...documentSignatureTags];

		if (value.documentValues === undefined) {
			value.documentValues = {};
		}
	};

	const updatePartyViewed = () => {
		const paths = (window?.location?.pathname || "")
			.split("/")
			.filter((e) => !!e);
		if (paths[0] === "sign") {
			const party = capitalizeEachWord(paths[1]);
			const files = Object.keys(value)
				.filter((key) => value[key].type !== undefined)
				.map((fileKey) => value[fileKey])
				.sort((a, b) => (a.index < b.index ? -1 : 1));

			for (const file of files) {
				if (!file.viewed[party]) return;
			}

			value[`${party} Viewed`] = new Date().toISOString();

			handleChange(question);
		}
	};
</script>

{#if !readonly}
	<div class="grid grid-cols-1 gap-4">
		<div class="lg:flex lg:gap-4">
			<div class="text-nowrap">Add Template:</div>
			{#if loading}
				<Loading />
			{:else}
				<div class="w-full">
					<Select
						disabled={readonly}
						name={`${label} template select`}
						{options}
						bind:value={newTemplate}
						handleChange={handleTemplateSelect}
					/>
				</div>
			{/if}
		</div>

		<div>
			{#if value?.documentValues}
				<TemplateFields
					bind:value
					bind:documentValues={value.documentValues}
					documents={Object.keys(value)
						.filter((k) => value[k]?.type !== undefined)
						.map((k) => value[k])}
					tags={value?.documentTags}
					label={question.label}
					{question}
					{handleChange}
				/>
			{/if}
		</div>
	</div>
{/if}

{#each previewFiles || [] as file}
	<div class="flex justify-between gap-2 lg:gap-4">
		<DocumentPreview
			{file}
			showDocPreview={readonly}
			{hideIframe}
			bind:viewed={file.viewed}
			{updatePartyViewed}
		/>

		{#if !readonly}
			<div class="my-auto">
				<button
					key={file.name}
					class={`btn btn-sm ${deleteConfirm[file.name] ? "btn-error animate-pulse" : "btn-warning"}`}
					on:click|preventDefault={deleteDocumentFunction}
				>
					{#if deleteConfirm[file.name]}
						<div class={`flex`}>
							<SvelteFa icon={faCircleCheck} size="1.5x" />
						</div>
						<!-- <span class="hidden sm:inline"> Confirm </span> -->
					{:else}
						<SvelteFa icon={faTrashCan} size="1.5x" />
						<!-- <span class="hidden sm:inline"> Delete </span> -->
					{/if}
				</button>
			</div>
		{/if}
	</div>
{/each}
